<template>
  <div class="permission-management-container">
    <a-card title="权限管理" :bordered="false">
      <div slot="extra">
        <a-button type="primary" @click="loadPermissionTree">刷新</a-button>
        <a-button 
          type="primary" 
          @click="showImportDialog" 
          style="margin-left: 10px;"
          icon="import">
          导入权限
        </a-button>
        <a-button 
          type="primary" 
          @click="exportPermissions" 
          style="margin-left: 10px;"
          icon="export">
          导出权限
        </a-button>
        <a-button type="primary" @click="openTenantPermissionDialog" style="margin-left: 10px;" icon="setting">租户权限分配</a-button>
        <a-button type="primary" @click="addPermission" style="margin-left: 10px;" icon="plus">新增权限</a-button>
      </div>

      <div class="main-content">
        <!-- 左侧：权限树 -->
        <div class="left-panel">
          <div class="panel-header">
            <h3>权限树结构</h3>
          </div>
          <div class="tree-container">
            <a-tree
              ref="permissionTree"
              :tree-data="permissionTreeData"
              :expanded-keys="expandedKeys"
              :selected-keys="selectedKeys"
              @select="handleTreeSelect"
              @rightClick="showContextMenu">
              <template slot="title" slot-scope="{ title, value, data }">
                <div class="custom-tree-node">
                  <span class="node-label">{{ title }}</span>
                  <span class="node-value">({{ value }})</span>
                  <div class="node-actions">
                    <a-button size="small" type="link" @click.stop="addChildPermission(data)">添加子权限</a-button>
                    <a-button size="small" type="link" @click.stop="editPermission(data)">编辑</a-button>
                    <a-popconfirm
                      title="确定要删除这个权限吗？"
                      @confirm="deletePermission(data)"
                      okText="确定"
                      cancelText="取消">
                      <a-button size="small" type="link" style="color: red;">删除</a-button>
                    </a-popconfirm>
                  </div>
                </div>
              </template>
            </a-tree>
          </div>
        </div>

        <!-- 右侧：编辑面板 -->
        <div class="right-panel">
          <div class="panel-header">
            <h3>{{ editMode === 'create' ? '新增权限' : editMode === 'edit' ? '编辑权限' : '权限详情' }}</h3>
          </div>
          <div class="form-container" v-if="selectedPermission || editMode === 'create'">
            <a-form :form="form" layout="vertical">
              <a-form-item label="权限名称">
                <a-input
                  v-decorator="['label', { rules: [{ required: true, message: '请输入权限名称' }] }]"
                  placeholder="请输入权限名称" />
              </a-form-item>
              
              <a-form-item label="权限值">
                <a-input
                  v-decorator="['value', { rules: [{ required: true, message: '请输入权限值' }] }]"
                  placeholder="请输入权限值" />
              </a-form-item>

              <a-form-item label="父级权限">
                <a-tree-select
                  v-decorator="['parent_id']"
                  :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
                  :tree-data="permissionTreeDataForSelect"
                  placeholder="请选择父级权限"
                  tree-default-expand-all
                  :replace-fields="{ title: 'title', value: 'value', children: 'children' }">
                </a-tree-select>
              </a-form-item>

              <a-form-item label="排序">
                <a-input-number
                  v-decorator="['sort_order', { initialValue: 0 }]"
                  :min="0"
                  style="width: 100%" />
              </a-form-item>

              <a-form-item label="可见性">
                <a-switch
                  v-decorator="['visible', { valuePropName: 'checked', initialValue: true }]"
                  checked-children="显示"
                  un-checked-children="隐藏" />
              </a-form-item>

              <a-form-item label="描述">
                <a-textarea
                  v-decorator="['description']"
                  placeholder="请输入权限描述"
                  :rows="3" />
              </a-form-item>

              <a-form-item>
                <a-button type="primary" @click="savePermission" :loading="saving">
                  {{ editMode === 'create' ? '创建权限' : '更新权限' }}
                </a-button>
                <a-button style="margin-left: 8px;" @click="cancelEdit">
                  取消
                </a-button>
                <a-button v-if="editMode === 'edit'" style="margin-left: 8px;" @click="deleteCurrentPermission">
                  删除
                </a-button>
                <a-button v-if="editMode === 'view' && selectedPermission" style="margin-left: 8px;" @click="startEdit">
                  编辑
                </a-button>
              </a-form-item>
            </a-form>
          </div>
          <div class="empty-state" v-else>
            <a-icon type="inbox" />
            <p>请选择一个权限进行编辑</p>
            <p>或点击"新增权限"创建新权限</p>
          </div>
        </div>
      </div>

      <!-- 右键菜单 -->
      <a-dropdown :trigger="['contextmenu']" placement="bottomLeft">
        <template slot="overlay">
          <a-menu @click="handleContextMenuClick">
            <a-menu-item key="add">添加子权限</a-menu-item>
            <a-menu-item key="edit">编辑权限</a-menu-item>
            <a-menu-item key="delete">删除权限</a-menu-item>
          </a-menu>
        </template>
        <div style="position: fixed; left: -1000px; top: -1000px;"></div>
      </a-dropdown>

      <!-- 租户权限分配对话框 -->
      <a-modal
        v-model="tenantPermissionDialogVisible"
        title="租户权限分配"
        width="800px"
        @ok="saveTenantPermissions"
        @cancel="tenantPermissionDialogVisible = false">
        <a-form layout="vertical">
          <a-form-item label="选择租户">
            <a-select
              v-model="selectedTenantId"
              placeholder="请选择要分配权限的租户"
              show-search
              :filter-option="filterTenantOption"
              @change="handleTenantChange"
              :loading="tenantLoading">
              <a-select-option
                v-for="tenant in tenantList"
                :key="tenant.id"
                :value="tenant.id">
                {{ tenant.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
          
          <a-form-item label="权限配置" v-if="selectedTenantId">
            <div class="tenant-permission-tree-container">
              <a-tree
                v-model="checkedTenantPermissionKeys"
                checkable
                :tree-data="fullPermissionTreeData"
                :expanded-keys="fullExpandedKeys"
                :replace-fields="{ title: 'title', key: 'value', children: 'children' }"
                @expand="handleFullTreeExpand">
              </a-tree>
            </div>
          </a-form-item>
          
          <a-alert 
            v-if="selectedTenantId" 
            :message="`已选择 ${checkedTenantPermissionKeys.length} 个权限`" 
            type="info" 
            show-icon 
            style="margin-top: 16px;" />
        </a-form>
      </a-modal>

      <!-- 导入权限对话框 -->
      <a-modal
        v-model="importDialogVisible"
        title="导入权限数据"
        width="800px"
        @ok="handleImport"
        @cancel="importDialogVisible = false"
        :confirmLoading="importLoading">
        <a-alert 
          message="请输入authority格式的JSON数据" 
          type="info" 
          show-icon 
          style="margin-bottom: 16px;">
          <div slot="description">
            <p>格式示例：</p>
            <pre style="background: #f5f5f5; padding: 8px; border-radius: 4px; font-size: 12px;">[
  {
    "value": "10001",
    "label": "系统管理",
    "visable": true,
    "children": [
      { "value": "10002", "label": "用户管理", "visable": true }
    ]
  }
]</pre>
          </div>
        </a-alert>
        <a-form-item label="JSON数据">
          <a-textarea
            v-model="importJsonText"
            placeholder="请粘贴JSON格式的权限数据"
            :rows="15"
            style="font-family: monospace;" />
        </a-form-item>
        <a-alert 
          v-if="importError" 
          :message="importError" 
          type="error" 
          show-icon 
          style="margin-top: 8px;" />
      </a-modal>

      <!-- 导出权限对话框 -->
      <a-modal
        v-model="exportDialogVisible"
        title="导出权限数据"
        width="800px"
        :footer="null"
        @cancel="exportDialogVisible = false">
        <div style="margin-bottom: 16px;">
          <a-button type="primary" icon="copy" @click="copyExportData" :loading="copyLoading">
            复制到剪贴板
          </a-button>
          <a-button style="margin-left: 8px;" icon="download" @click="downloadExportData">
            下载为文件
          </a-button>
          <span style="margin-left: 16px; color: #888;">提示：可全选文本后 Ctrl+C 复制</span>
        </div>
        <a-input
          type="textarea"
          v-model="exportJsonText"
          :rows="20"
          readonly
          style="font-family: monospace; font-size: 12px;"
          placeholder="导出的权限数据将显示在这里" />
      </a-modal>
    </a-card>
  </div>
</template>

<script>
export default {
  name: 'PermissionManagement',
  data() {
    return {
      form: this.$form.createForm(this),
      permissionTreeData: [],
      permissionTreeDataForSelect: [],
      fullPermissionTreeData: [], // 完整权限树（用于租户权限分配）
      expandedKeys: [],
      fullExpandedKeys: [], // 完整树的展开keys
      selectedKeys: [],
      selectedPermission: null,
      editMode: 'view', // 'view', 'create', 'edit'
      saving: false,
      
      // 租户权限分配相关
      tenantPermissionDialogVisible: false,
      checkedTenantPermissionKeys: [],
      tenantList: [], // 租户列表
      selectedTenantId: null, // 选中的租户ID
      tenantLoading: false, // 租户加载状态
      
      // 右键菜单相关
      contextMenuVisible: false,
      contextMenuX: 0,
      contextMenuY: 0,
      contextMenuNode: null,
      
      // 导入导出相关
      importDialogVisible: false,
      importJsonText: '',
      importError: '',
      importLoading: false,
      exportDialogVisible: false,
      exportJsonText: '',
      copyLoading: false
    }
  },
  
  async mounted() {
    await this.loadPermissionTree()
    await this.loadFullPermissionTree() // 加载完整权限树用于租户分配
  },
  
  methods: {
    async loadPermissionTree() {
      try {
        const response = await this.$api.getPermissionTree()
        const treeData = response.data || []
        this.permissionTreeData = this.convertToAntTreeData(treeData)
        this.permissionTreeDataForSelect = this.convertToSelectData(treeData)
        this.expandedKeys = this.getAllKeys(this.permissionTreeData)
      } catch (error) {
        console.error('加载权限树失败:', error)
        this.$message.error('加载权限树失败')
      }
    },
    
    // 加载完整权限树（用于租户权限分配）
    async loadFullPermissionTree() {
      try {
        // 从后端获取最新的权限树数据
        const response = await this.$api.getPermissionTree()
        const treeData = response.data || []
        this.fullPermissionTreeData = this.convertToAntTreeData(treeData)
        this.fullExpandedKeys = this.getAllKeys(this.fullPermissionTreeData)
      } catch (error) {
        console.error('加载完整权限树失败:', error)
        this.$message.error('无法获取权限数据')
      }
    },
    
    convertToAntTreeData(nodes) {
      return nodes.map(node => {
        const treeNode = {
          title: `${node.label} (${node.value})`,
          value: node.value,
          key: node.value,
          data: node,
          scopedSlots: { title: 'title' }
        }
        
        if (node.children && node.children.length) {
          treeNode.children = this.convertToAntTreeData(node.children)
        }
        
        return treeNode
      })
    },
    
    convertToSelectData(nodes) {
      return nodes.map(node => {
        const result = {
          title: `${node.label} (${node.value})`, // 使用title字段显示完整信息
          value: node.id, // 使用id作为value，这样保存时存储的是权限ID而不是权限值
          label: `${node.label} (${node.value})`, // 保持label字段用于兼容
          disabled: this.selectedPermission && node.value === this.selectedPermission.value
        }
        
        if (node.children && node.children.length) {
          result.children = this.convertToSelectData(node.children)
        }
        
        return result
      })
    },
    
    getAllKeys(treeData) {
      const keys = []
      const traverse = (nodes) => {
        nodes.forEach(node => {
          keys.push(node.key)
          if (node.children) {
            traverse(node.children)
          }
        })
      }
      traverse(treeData)
      return keys
    },
    
    handleTreeSelect(selectedKeys, { selected, node }) {
      if (selected && selectedKeys.length > 0) {
        this.selectedKeys = selectedKeys
        this.selectedPermission = node.data
        this.editMode = 'view'
        console.log('Selected permission data:', this.selectedPermission) // 调试日志
        
        // 使用setTimeout确保DOM完全更新后再设置表单值
        setTimeout(() => {
          this.setFormValues(this.selectedPermission)
        }, 100)
      } else {
        this.selectedKeys = []
        this.selectedPermission = null
        this.editMode = 'view'
        this.form.resetFields()
      }
    },
    
    showContextMenu({ event, node }) {
      event.preventDefault()
      this.contextMenuNode = node
      this.contextMenuX = event.clientX
      this.contextMenuY = event.clientY
      this.contextMenuVisible = true
    },
    
    handleContextMenuClick({ key }) {
      const nodeData = this.contextMenuNode.data
      switch (key) {
        case 'add':
          this.addChildPermission(nodeData)
          break
        case 'edit':
          this.editPermission(nodeData)
          break
        case 'delete':
          this.deletePermission(nodeData)
          break
      }
      this.contextMenuVisible = false
    },
    
    addPermission() {
      this.editMode = 'create'
      this.selectedPermission = null
      this.selectedKeys = []
      this.form.resetFields()
    },
    
    addChildPermission(parentId) {
      this.editMode = 'create'
      this.selectedPermission = null
      this.selectedKeys = []
      this.form.resetFields()
      this.form.setFieldsValue({
        parent_id: parentId
      })
    },
    
    editPermission(permission) {
      this.selectedKeys = [permission.value]
      this.selectedPermission = permission
      this.editMode = 'edit'
      this.setFormValues(permission)
    },
    
    setFormValues(permission) {
      console.log('Setting form values for permission:', permission) // 调试日志
      
      if (!permission) {
        console.warn('Permission data is null or undefined')
        return
      }
      
      try {
        // 先重置表单
        this.form.resetFields()
        
        // 设置表单值
        this.$nextTick(() => {
          this.form.setFieldsValue({
            label: permission.label || '',
            value: permission.value || '',
            parent_id: permission.parent_id || undefined,
            sort_order: permission.sort_order !== undefined ? permission.sort_order : 0,
            visible: permission.visible !== undefined ? permission.visible : true,
            description: permission.description || ''
          })
          console.log('Form values after setting:', this.form.getFieldsValue()) // 调试日志
        })
      } catch (error) {
        console.error('Error setting form values:', error)
      }
    },
    
    findPermissionById(nodes, id) {
      for (const node of nodes) {
        if (node.data.value === id) {
          return node.data
        }
        if (node.children) {
          const found = this.findPermissionById(node.children, id)
          if (found) return found
        }
      }
      return null
    },
    
    async savePermission() {
      try {
        const values = await this.form.validateFields()
        
        const permissionData = {
          label: values.label,
          value: values.value,
          parent_id: values.parent_id,
          sort_order: values.sort_order,
          visible: values.visible,
          description: values.description
        }
        
        if (this.editMode === 'edit' && this.selectedPermission) {
          await this.$api.updatePermission(this.selectedPermission.id, permissionData)
          this.$message.success('更新权限成功')
        } else {
          await this.$api.createPermission(permissionData)
          this.$message.success('创建权限成功')
        }
        
        this.cancelEdit()
        await this.loadPermissionTree()
      } catch (error) {
        if (error.errorFields) {
          return
        }
        console.error('保存权限失败:', error)
        this.$message.error(error.message || '保存权限失败')
      }
    },
    
    cancelEdit() {
      this.editMode = 'view'
      this.selectedPermission = null
      this.selectedKeys = []
      this.form.resetFields()
    },
    
    startEdit() {
      if (this.selectedPermission) {
        this.editMode = 'edit'
      }
    },
    
    async deletePermission(permission) {
      try {
        await this.$api.deletePermission(permission.id)
        this.$message.success('删除权限成功')
        this.cancelEdit()
        await this.loadPermissionTree()
      } catch (error) {
        console.error('删除权限失败:', error)
        this.$message.error(error.message || '删除权限失败')
      }
    },
    
    deleteCurrentPermission() {
      if (this.selectedPermission) {
        this.deletePermission(this.selectedPermission)
      }
    },
    
    // 租户权限分配相关方法
    async openTenantPermissionDialog() {
      this.tenantPermissionDialogVisible = true
      this.selectedTenantId = null
      this.checkedTenantPermissionKeys = []
      await this.loadTenantList()
      await this.loadFullPermissionTree() // 确保权限树已加载
    },
    
    // 加载租户列表
    async loadTenantList() {
      try {
        this.tenantLoading = true
        // 调用租户列表 API（使用超管专用的 tenants 接口）
        const response = await this.$api.get_tenants_list({ page_size: 1000 })
        console.log('租户列表 API 响应:', response)
            
        if (response.code === 200 && response.data) {
          // data 是数组，直接使用
          this.tenantList = response.data.map(tenant => ({
            id: tenant.id,
            name: tenant.name
          }))
          console.log('处理后的租户数据:', this.tenantList)
        } else {
          // 如果 API 不可用，显示空列表
          this.tenantList = []
          this.$message.warning('无法获取租户列表')
        }
      } catch (error) {
        console.error('加载租户列表失败:', error)
        this.tenantList = []
        this.$message.error('加载租户列表失败')
      } finally {
        this.tenantLoading = false
      }
    },
    
    // 处理租户选择变化
    async handleTenantChange(tenantId) {
      this.selectedTenantId = tenantId
      this.checkedTenantPermissionKeys = []
      if (tenantId) {
        await this.loadTenantPermissions(tenantId)
      }
    },
    
    async loadTenantPermissions(tenantId) {
      try {
        const response = await this.$api.getTenantPermissions(tenantId)
        if (response.code === 200) {
          this.checkedTenantPermissionKeys = response.data || []
        }
      } catch (error) {
        console.error('获取租户权限失败:', error)
        this.checkedTenantPermissionKeys = []
      }
    },
    
    async saveTenantPermissions() {
      if (!this.selectedTenantId) {
        this.$message.warning('请先选择租户')
        return
      }
      
      // 调试日志
      console.log('Sending tenant permissions data:', {
        tenantId: this.selectedTenantId,
        permissions: this.checkedTenantPermissionKeys,
        dataType: typeof this.checkedTenantPermissionKeys,
        isArray: Array.isArray(this.checkedTenantPermissionKeys)
      })
      
      try {
        // 按照后端期望的格式发送数据
        await this.$api.updateTenantPermissions(this.selectedTenantId, {
          permissions: this.checkedTenantPermissionKeys
        })
        this.$message.success('租户权限分配成功')
        this.tenantPermissionDialogVisible = false
      } catch (error) {
        console.error('保存租户权限失败:', error)
        this.$message.error('保存租户权限失败')
      }
    },
    
    handleFullTreeExpand(expandedKeys) {
      this.fullExpandedKeys = expandedKeys
    },
    
    // 租户选项过滤方法
    filterTenantOption(input, option) {
      return option.componentOptions.childrenText.toLowerCase().indexOf(input.toLowerCase()) >= 0
    },
    
    // 显示导入对话框
    showImportDialog() {
      this.importDialogVisible = true
      this.importJsonText = ''
      this.importError = ''
    },
    
    // 处理导入
    async handleImport() {
      this.importError = ''
      
      // 验证JSON格式
      let jsonData
      try {
        jsonData = JSON.parse(this.importJsonText)
      } catch (e) {
        this.importError = 'JSON格式无效，请检查语法错误'
        return
      }
      
      // 验证是数组
      if (!Array.isArray(jsonData)) {
        this.importError = '数据必须是数组格式'
        return
      }
      
      // 调用导入API
      this.importLoading = true
      try {
        const response = await this.$api.importPermissions(jsonData)
        if (response.code === 200) {
          const result = response.data
          this.$message.success(`导入完成：新增 ${result.imported} 条，更新 ${result.updated} 条`)
          if (result.errors && result.errors.length > 0) {
            this.$message.warning(`有 ${result.errors.length} 条记录导入失败`)
            console.warn('导入错误:', result.errors)
          }
          this.importDialogVisible = false
          await this.loadPermissionTree()
        } else {
          this.importError = response.message || '导入失败'
        }
      } catch (error) {
        console.error('导入权限失败:', error)
        this.importError = error.message || '导入失败'
      } finally {
        this.importLoading = false
      }
    },
    
    // 导出权限
    async exportPermissions() {
      try {
        const response = await this.$api.exportPermissions()
        if (response.code === 200) {
          this.exportJsonText = JSON.stringify(response.data, null, 2)
          this.exportDialogVisible = true
        } else {
          this.$message.error(response.message || '导出失败')
        }
      } catch (error) {
        console.error('导出权限失败:', error)
        this.$message.error(error.message || '导出失败')
      }
    },
    
    // 复制导出数据到剪贴板
    async copyExportData() {
      this.copyLoading = true
      try {
        await navigator.clipboard.writeText(this.exportJsonText)
        this.$message.success('已复制到剪贴板')
      } catch (error) {
        // 降级方案：使用传统复制方法
        try {
          const textarea = document.createElement('textarea')
          textarea.value = this.exportJsonText
          textarea.style.position = 'fixed'
          textarea.style.left = '-9999px'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
          this.$message.success('已复制到剪贴板')
        } catch (e) {
          this.$message.error('复制失败，请手动选择文本复制')
        }
      } finally {
        this.copyLoading = false
      }
    },
    
    // 下载导出数据为文件
    downloadExportData() {
      const blob = new Blob([this.exportJsonText], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `permissions_${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      this.$message.success('文件下载成功')
    }
  }
}
</script>

<style lang="scss" scoped>
.permission-management-container {
  height: 100%;
  
  ::v-deep .ant-card {
    height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
    
    .ant-card-head {
      background: #fafafa;
      border-bottom: 1px solid #e8e8e8;
      
      .ant-card-head-title {
        color: rgba(0, 0, 0, 0.85);
        font-size: 16px;
        font-weight: 500;
      }
    }
    
    .ant-card-body {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 0;
    }
  }
  
  .main-content {
    flex: 1;
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 20px;
    padding: 20px;
    overflow: hidden;
    
    .panel-header {
      padding: 0 0 16px 0;
      border-bottom: 1px solid #e8e8e8;
      margin-bottom: 16px;
      
      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.85);
      }
    }
    
    .left-panel {
      background: #fff;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      
      .tree-container {
        flex: 1;
        overflow-y: auto;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        background: #fafafa;
        
        ::v-deep .ant-tree {
          background: transparent;
          padding: 12px;
          
          .ant-tree-treenode {
            padding: 4px 0;
            
            .ant-tree-node-content-wrapper {
              height: 32px;
              line-height: 30px;
              border-radius: 2px;
              
              &:hover {
                background-color: #f5f5f5;
              }
            }
          }
          
          .ant-tree-treenode-selected .ant-tree-node-content-wrapper {
            background-color: #e6f7ff;
            color: #1890ff;
          }
        }
      }
    }
    
    .right-panel {
      background: #fff;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      
      .form-container {
        flex: 1;
        overflow-y: auto;
        padding: 8px;
        
        ::v-deep .ant-form-item {
          margin-bottom: 20px;
          
          .ant-form-item-label label {
            font-weight: 500;
            color: rgba(0, 0, 0, 0.85);
          }
          
          .ant-input, .ant-select-selection, .ant-input-number {
            border-radius: 4px;
            border: 1px solid #d9d9d9;
            
            &:focus, &:hover {
              border-color: #40a9ff;
              box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
            }
          }
          
          .ant-switch {
            background: #ccc;
            
            &.ant-switch-checked {
              background: #1890ff;
            }
          }
        }
        
        .ant-btn {
          border-radius: 4px;
          font-weight: 400;
          
          &.ant-btn-primary {
            background: #1890ff;
            border-color: #1890ff;
            
            &:hover {
              background: #40a9ff;
              border-color: #40a9ff;
            }
          }
        }
      }
      
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 300px;
        color: rgba(0, 0, 0, 0.45);
        background: #fafafa;
        border: 1px dashed #d9d9d9;
        border-radius: 4px;
        
        .anticon {
          font-size: 48px;
          margin-bottom: 16px;
          color: #bfbfbf;
        }
        
        p {
          margin: 8px 0;
          font-size: 14px;
          color: rgba(0, 0, 0, 0.45);
        }
      }
    }
  }
  
  .custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    padding-right: 8px;
    width: 100%;
    
    .node-label {
      font-weight: 400;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: rgba(0, 0, 0, 0.65);
    }
    
    .node-value {
      color: rgba(0, 0, 0, 0.45);
      font-size: 12px;
      margin: 0 10px;
      flex-shrink: 0;
      background: rgba(0, 0, 0, 0.04);
      padding: 2px 6px;
      border-radius: 2px;
    }
    
    .node-actions {
      display: flex;
      gap: 6px;
      flex-shrink: 0;
      opacity: 0;
      transition: opacity 0.2s;
      
      .ant-btn {
        padding: 0 6px;
        height: 22px;
        font-size: 12px;
        border-radius: 2px;
        
        &.ant-btn-link {
          color: #1890ff;
          
          &:hover {
            color: #40a9ff;
            background: rgba(24, 144, 255, 0.1);
          }
        }
      }
    }
    
    &:hover .node-actions {
      opacity: 1;
    }
  }
}

@media (max-width: 1200px) {
  .main-content {
    grid-template-columns: 1fr 2fr;
  }
}

@media (max-width: 768px) {
  .main-content {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px;
    
    .left-panel, .right-panel {
      height: auto;
      min-height: 300px;
    }
  }
  
  .permission-management-container ::v-deep .ant-card {
    height: auto;
    min-height: calc(100vh - 60px);
  }
}

.tenant-permission-tree-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px;
  background: #fafafa;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
  
  ::v-deep .ant-tree {
    background: transparent;
    
    .ant-tree-treenode {
      padding: 4px 0;
      
      .ant-tree-node-content-wrapper {
        &:hover {
          background-color: #f5f5f5;
        }
      }
    }
    
    .ant-tree-checkbox {
      margin-right: 8px;
    }
  }
}
</style>